import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

import { DashboardShell } from "@/components/DashboardShell";
import { PARTNER_NAV } from "@/lib/nav";
import { useAuth } from "@/lib/auth";
import type { ServiceType } from "@/lib/services";
import { supabase } from "@/integrations/supabase/client";
import {
  ENQUIRY_STATUS_CLASS,
  ENQUIRY_STATUS_LABEL,
  closeEnquiry,
  markEnquiryRead,
  normalizeStatus,
  replyToEnquiry,
  type Enquiry,
} from "@/lib/enquiries";

export const Route = createFileRoute(
  "/_authenticated/partner/listings/$service/$listingId/enquiries",
)({
  head: () => ({
    meta: [
      { title: "Listing enquiries · Landoo" },
      { name: "description", content: "Read, reply to and close enquiries for a single listing." },
    ],
  }),
  component: ListingEnquiriesPage,
});

async function fetchListingEnquiries(partnerId: string, listingId: string): Promise<Enquiry[]> {
  const { data, error } = await supabase
    .from("partner_enquiries")
    .select("*")
    .eq("partner_id", partnerId)
    .eq("listing_id", listingId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return ((data as unknown as Enquiry[]) ?? []).map((r) => ({
    ...r,
    enquiry_status: normalizeStatus(r.enquiry_status),
  }));
}

function ListingEnquiriesPage() {
  const { service, listingId } = Route.useParams() as { service: ServiceType; listingId: string };
  const { user } = useAuth();
  const qc = useQueryClient();
  const [openId, setOpenId] = useState<string | null>(null);
  const [reply, setReply] = useState("");

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["partner", "listing-enquiries", user?.id, listingId],
    queryFn: () => fetchListingEnquiries(user!.id, listingId),
    enabled: !!user,
  });

  const refresh = () =>
    qc.invalidateQueries({ queryKey: ["partner", "listing-enquiries", user?.id, listingId] });

  const readMutation = useMutation({ mutationFn: markEnquiryRead, onSuccess: refresh });
  const replyMutation = useMutation({
    mutationFn: ({ id, text }: { id: string; text: string }) => replyToEnquiry(id, text),
    onSuccess: () => {
      toast.success("Reply sent");
      setOpenId(null);
      setReply("");
      refresh();
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Could not send reply"),
  });
  const closeMutation = useMutation({
    mutationFn: closeEnquiry,
    onSuccess: () => {
      toast.success("Enquiry closed");
      refresh();
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Could not close"),
  });

  const openReply = (e: Enquiry) => {
    setOpenId(e.id);
    setReply(e.partner_reply ?? "");
    if (e.enquiry_status === "new") readMutation.mutate(e.id);
  };

  const listingTitle = rows[0]?.listing_title ?? "this listing";

  return (
    <DashboardShell title="Listing enquiries" role="Partner" items={PARTNER_NAV}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <Link
          to="/partner/listings"
          className="inline-flex items-center gap-1 rounded-full bg-secondary px-3 py-1.5 text-xs font-semibold text-brown-dark"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to My listings
        </Link>
        <Link
          to="/partner/listings/$service/$listingId"
          params={{ service, listingId }}
          className="rounded-full bg-brand px-3 py-1.5 text-xs font-semibold text-brown-dark"
        >
          Edit listing
        </Link>
      </div>

      <div className="rounded-2xl border border-border bg-card p-4 shadow-soft">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Enquiries for
        </p>
        <h2 className="mt-0.5 text-sm font-bold text-brown-dark">{listingTitle}</h2>
        <p className="text-[11px] text-muted-foreground">{rows.length} total</p>
      </div>

      {isLoading ? (
        <p className="mt-4 text-sm text-muted-foreground">Loading enquiries…</p>
      ) : rows.length === 0 ? (
        <div className="mt-4 rounded-2xl border border-dashed border-border bg-card p-6 text-sm text-muted-foreground">
          No enquiries on this listing yet.
        </div>
      ) : (
        <div className="mt-4 space-y-2">
          {rows.map((e) => (
            <div key={e.id} className="rounded-2xl border border-border bg-card p-4 shadow-soft">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span
                  className={`rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider ${ENQUIRY_STATUS_CLASS[e.enquiry_status]}`}
                >
                  {ENQUIRY_STATUS_LABEL[e.enquiry_status]}
                </span>
                <span className="text-[11px] text-muted-foreground">
                  {new Date(e.created_at).toLocaleString()}
                </span>
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                {e.customer_name ?? "Customer"} · {e.customer_contact ?? "no mobile"}
              </div>
              {e.message && <p className="mt-1.5 text-xs text-brown-dark">{e.message}</p>}
              {e.partner_reply && (
                <div className="mt-2 rounded-xl bg-secondary p-2.5">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Your reply
                  </p>
                  <p className="mt-1 text-xs text-brown-dark">{e.partner_reply}</p>
                </div>
              )}

              {openId === e.id ? (
                <div className="mt-3 space-y-2">
                  <textarea
                    value={reply}
                    onChange={(ev) => setReply(ev.target.value)}
                    rows={3}
                    maxLength={1000}
                    placeholder="Write your reply…"
                    className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:border-brand"
                  />
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => replyMutation.mutate({ id: e.id, text: reply })}
                      disabled={reply.trim().length < 2 || replyMutation.isPending}
                      className="rounded-xl bg-brand px-4 py-2 text-xs font-bold text-brown-dark disabled:opacity-60"
                    >
                      Send reply
                    </button>
                    <button
                      type="button"
                      onClick={() => setOpenId(null)}
                      className="rounded-xl border border-border px-4 py-2 text-xs font-semibold text-brown-dark"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                e.enquiry_status !== "closed" && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => openReply(e)}
                      className="rounded-xl bg-brand px-4 py-2 text-xs font-bold text-brown-dark"
                    >
                      {e.partner_reply ? "Edit reply" : "Reply"}
                    </button>
                    <button
                      type="button"
                      onClick={() => closeMutation.mutate(e.id)}
                      disabled={closeMutation.isPending}
                      className="rounded-xl border border-border px-4 py-2 text-xs font-semibold text-brown-dark disabled:opacity-60"
                    >
                      Close enquiry
                    </button>
                  </div>
                )
              )}
            </div>
          ))}
        </div>
      )}
    </DashboardShell>
  );
}
